import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Eye, Copy, Trash, PencilSimple, PaperPlaneTilt, FilePdf, FileText, MagnifyingGlass, ArrowClockwise, Plus } from "@phosphor-icons/react";

const STATUS_CONFIG = {
  borrador: { label: "Borrador", color: "bg-slate-100 text-slate-700" },
  pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-800" },
  aprobado: { label: "Aprobado", color: "bg-green-100 text-green-800" },
  rechazado: { label: "Rechazado", color: "bg-red-100 text-red-800" },
  vencido: { label: "Vencido", color: "bg-gray-100 text-gray-600" },
};

export default function QuotationList({ token, onNavigate, initialFilter }) {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialFilter || "todos");

  const fetchQuotations = useCallback(async () => {
    try {
      const params = statusFilter !== "todos" ? `?status=${statusFilter}` : "";
      const { data } = await axios.get(`${API}/quotations${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotations(data);
    } catch { toast.error("Error al cargar cotizaciones"); }
    finally { setLoading(false); }
  }, [token, statusFilter]);

  useEffect(() => { fetchQuotations(); }, [fetchQuotations]);

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API}/quotations/${id}/duplicate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Cotizacion duplicada");
      fetchQuotations();
    } catch { toast.error("Error al duplicar"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta cotizacion?")) return;
    try {
      await axios.delete(`${API}/quotations/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Cotizacion eliminada");
      fetchQuotations();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleSend = async (id) => {
    try {
      await axios.post(`${API}/quotations/${id}/send`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Cotizacion enviada");
      fetchQuotations();
    } catch { toast.error("Error al enviar"); }
  };

  const handlePdf = async (id) => {
    try {
      const res = await axios.get(`${API}/quotations/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `cotizacion_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al generar PDF"); }
  };

  const filtered = quotations.filter(q =>
    (q.client_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (q.quotation_number || "").toLowerCase().includes(search.toLowerCase()) ||
    (q.client_company || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="quotation-list">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-slate-800">Presupuestos</h2>
        <Button onClick={() => onNavigate("create")} className="bg-blue-600 hover:bg-blue-700" data-testid="create-quotation-btn-list">
          <Plus size={16} className="mr-1" /> Nuevo
        </Button>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Buscar por cliente, empresa o numero..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none" data-testid="quotation-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="quotation-filter">
          <option value="todos">Todos</option>
          <option value="borrador">Borrador</option>
          <option value="pendiente">Pendiente</option>
          <option value="aprobado">Aprobado</option>
          <option value="rechazado">Rechazado</option>
          <option value="vencido">Vencido</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><ArrowClockwise size={28} className="animate-spin text-blue-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <FileText size={48} className="mx-auto mb-3 opacity-30" weight="thin" />
          <p>No hay cotizaciones</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 font-semibold text-slate-600">No.</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Cliente</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Empresa</th>
                <th className="px-4 py-3 font-semibold text-slate-600 text-right">Total MXN</th>
                <th className="px-4 py-3 font-semibold text-slate-600 text-right">Total USD</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Estado</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Fecha</th>
                <th className="px-4 py-3 font-semibold text-slate-600 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(q => (
                <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-blue-600 font-semibold">{q.quotation_number}</td>
                  <td className="px-4 py-3">{q.client_name}</td>
                  <td className="px-4 py-3 text-slate-500">{q.client_company || "-"}</td>
                  <td className="px-4 py-3 text-right font-semibold">${(q.total_mxn || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 text-right text-slate-500">${(q.total_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[q.status]?.color || "bg-slate-100"}`}>
                      {STATUS_CONFIG[q.status]?.label || q.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{(q.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => onNavigate("detail", null, q.id)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Ver" data-testid={`view-q-${q.id}`}><Eye size={16} /></button>
                      {q.status === "borrador" && <button onClick={() => handleSend(q.id)} className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Enviar" data-testid={`send-q-${q.id}`}><PaperPlaneTilt size={16} /></button>}
                      {q.status === "borrador" && <button onClick={() => onNavigate("edit", null, q.id)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Editar"><PencilSimple size={16} /></button>}
                      <button onClick={() => handlePdf(q.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="PDF"><FilePdf size={16} /></button>
                      <button onClick={() => handleDuplicate(q.id)} className="p-1.5 rounded hover:bg-purple-50 text-purple-600" title="Duplicar"><Copy size={16} /></button>
                      <button onClick={() => handleDelete(q.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Eliminar"><Trash size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
